/**
 * Shared sysadmin Catalog entity-tab bar.
 *
 * The catalog's primary horizontal nav — Models · Functions · MCP · Tools ·
 * Workflows · Agents · Skills · Prompts. Rendered by BOTH the Catalog layout
 * (catalog.tsx) AND the Models layout (models.tsx).
 *
 * Why both: the Models tab deep-links OUT to the legacy /sysadmin/models route
 * tree (its physical relocation under /sysadmin/catalog is deferred). That tree
 * renders its own section sub-tabs but NOT this entity bar — so clicking Models
 * used to make the entity nav vanish, stranding the operator with no way back
 * to Functions/Tools/Skills. Mounting this shared bar in the Models layout too
 * keeps the entity nav persistent everywhere in the catalog; the Models section
 * sub-tabs (Catalog/Functions/Trust Toggles/Assignments/Runtime) render below.
 */

import { Link, useLocation } from '@tanstack/react-router';
import {
  Cpu,
  Network,
  Workflow,
  Bot,
  Sparkles,
  FileType,
  Code2,
  Wrench,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const CATALOG_ENTITY_TABS: Array<{
  key: string; label: string; icon: typeof Cpu; to: string;
  status: 'v1' | 'v2' | 'future';
  external?: string;
}> = [
  { key: 'models',     label: 'Models',     icon: Cpu,      to: '/sysadmin/catalog/models',     status: 'v1', external: '/sysadmin/models' },
  { key: 'functions',  label: 'Functions',  icon: Code2,    to: '/sysadmin/catalog/functions',  status: 'v1' },
  { key: 'mcp',        label: 'MCP',        icon: Network,  to: '/sysadmin/catalog/mcp',        status: 'v1' },
  { key: 'tools',      label: 'Tools',      icon: Wrench,   to: '/sysadmin/catalog/tools',      status: 'v1' },
  { key: 'workflows',  label: 'Workflows',  icon: Workflow, to: '/sysadmin/catalog/workflows',  status: 'v1' },
  { key: 'agents',     label: 'Agents',     icon: Bot,      to: '/sysadmin/catalog/agents',     status: 'v1' },
  { key: 'skills',     label: 'Skills',     icon: Sparkles, to: '/sysadmin/catalog/skills',     status: 'v1' },
  { key: 'prompts',    label: 'Prompts',    icon: FileType, to: '/sysadmin/catalog/prompts',    status: 'v1' },
];

export function CatalogEntityTabBar() {
  const location = useLocation();

  return (
    <div className="border-b sticky top-0 bg-background z-10 shrink-0">
      <nav className="flex items-center gap-0.5 overflow-x-auto px-6">
        {CATALOG_ENTITY_TABS.map((t) => {
          const Icon = t.icon;
          const target = t.external ?? t.to;
          const active = location.pathname === target ||
            location.pathname.startsWith(target + '/');
          return (
            <Link
              key={t.key}
              to={target}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                active
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-4 w-4" />
              {t.label}
              {t.status !== 'v1' && (
                <Badge variant="secondary" className="ml-1 text-[10px]">
                  {t.status}
                </Badge>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
